// © Microsoft Corporation. All rights reserved.

import Popover, { PopoverProps } from "../popover/Popover";
import { PopupConnector, UsersConnector } from "../../Social.props";
import { ReportMenuTelemetryObjects, reportMenuTelemetryActionDispatchers, reportMenuTelemetryContracts } from "./ReportMenu.telemetry";
import { SocialService, User } from "@msnews/social-data-service";
import { addOrUpdateTelemetryObject, renderHybrid } from "../../Social.telemetry";

import AccessibilityHelpers from "../../utils/AccessibilityHelpers";
import React from "react";
import { SocialClassNameContract } from "../../Social.classnames";
import { SocialLocalizedStrings } from "../../Social.localizedStrings";
import { TelemetryObjectBase } from "@msnews/telemetry-contracts";
import { classNames } from "@microsoft/fast-web-utilities";
import { enums } from "../../utils/enums";

export interface ReportMenuProps extends PopoverProps {
    managedClasses?: SocialClassNameContract;
    onReport?: (key: string, e: any) => void;
    t: SocialLocalizedStrings;
    mainTelemetryObject?: TelemetryObjectBase;
    theme?: string;
    popupConnector?: PopupConnector;
    usersConnector?: UsersConnector;
    id?: string;
    renderInPopover?: boolean;
    me?: User;
}

export default class ReportMenu extends React.Component<ReportMenuProps> {
    telemetryObjects: ReportMenuTelemetryObjects;
    containerRef: HTMLUListElement;

    constructor(props) {
        super(props);
        this.containerRef = null;
    }

    componentDidMount() {
        AccessibilityHelpers.attachAccessibilityListeners(document);

        if (!this.props.me) { this.props.usersConnector.fetchMe(); }
    }

    render() {
        if (!this.props.me) { return null; }

        /** Generate Telemetry Objects */
        this.telemetryObjects = {
            reportItems: (this.telemetryObjects && this.telemetryObjects.reportItems) || [],
            reportMenu: addOrUpdateTelemetryObject(this.props.mainTelemetryObject, reportMenuTelemetryContracts.reportMenu, { id: this.props.id || ""}, this.telemetryObjects && this.telemetryObjects.reportMenu)
        };

        return this.props.renderInPopover ?  this.renderDefault() : this.renderMobile();
    }

    renderDefault() {
        return (
            <Popover {...{...this.props, className: this.props.managedClasses.reportMenuPopover, hasFocusLock: true}}>
                <ul role="listbox"
                    aria-label={this.props.t.AriaLabelsOpenReportCommentMenu}
                    className={classNames(this.props.managedClasses.reportMenuContainer, this.props.responsiveClass)}
                    {...renderHybrid(this.telemetryObjects && this.telemetryObjects.reportMenu, true, (el) => { this.containerRef = el; })}
                >
                    {this.renderReportItems()}
                </ul>
            </Popover>
        );
    }

    renderMobile() {
        return (
            <ul role="listbox"
                aria-label={this.props.t.AriaLabelsOpenReportCommentMenu}
                className={classNames(
                    this.props.managedClasses.reportMenuContainer,
                    this.props.managedClasses.reportMenuSuperAppContainer,
                    this.props.responsiveClass
                )}
                {...renderHybrid(this.telemetryObjects && this.telemetryObjects.reportMenu, true, (el) => { this.containerRef = el; })}
            >
                <i className="cwt-icon cwt-menu-header" id="cwt-menu-header-username">
                    <span className="cwt-icon-label">{ this.props.t.CommentReportText }</span>
                </i>
                {this.renderReportItems()}
            </ul>
        );
    }

    renderReportItems() {
        return Object.keys(enums.commentReportMap).map(key => {
            if (this.telemetryObjects.reportItems) {
                this.telemetryObjects.reportItems[key] = addOrUpdateTelemetryObject(this.telemetryObjects.reportMenu, {...reportMenuTelemetryContracts.reportItem}, { id: this.props.id, headline: key }, this.telemetryObjects.reportItems[key]);
            }

            return <li role="option" className="cwt-access-li" key={key} onClick={ e => this.reportHandler(key, e) }  onKeyDown={(e) => { this.handleKeyDown(e); }} {...renderHybrid(this.telemetryObjects && this.telemetryObjects.reportItems && this.telemetryObjects.reportItems[key], false)}>
                {this.props.t[enums.commentReportMap[key]]}
            </li>;
        });
    }

    reportHandler(reportType, e) {
        reportMenuTelemetryActionDispatchers.reportItemClick(e.currentTarget, this.telemetryObjects && this.telemetryObjects.reportItems && this.telemetryObjects.reportItems[reportType]);
        const reportValue = enums.commentReportMap[reportType];
        if (reportValue == enums.commentReportMap.Copyright) {
            this.props.popupConnector.closePopup({disableBridge: true});
            if (this.props.me.state == enums.userStateType.anonymous) {
                this.props.popupConnector.openPopup("multiLoginPage");
            } else {
                this.props.popupConnector.openPopup(
                    reportValue == enums.commentReportMap.Copyright ? "copyrightform" : "",
                    {id: this.props.id, fullScreen: false, onReport: this.props.onReport});
            }
        } else {
            SocialService.reportComment(this.props.id, reportType);
            this.props.onReport && this.props.onReport(reportType, e);
            this.props.popupConnector.closePopup();
        }
    }

    handleKeyDown(e) {
        if (e.keyCode == 27) { /* ESC key */
            const ref = window.document.getElementById(`reportMenu_${this.props.id}`);
            if (ref) {
                window.setTimeout(()=> { ref.focus(); }, 50);
            }
            e.stopPropagation();
            e.preventDefault();
        } else if (e.keyCode == 38) { /* Up arrow */
            e.preventDefault();
            e.stopPropagation();
            const prev = e.target.previousElementSibling;
            if (prev) {
                prev.focus();
            } else {
                e.target.parentElement.lastElementChild.focus();
            }
        } else if (e.keyCode == 40)  { /* Down arrow */
            const next = e.target.nextElementSibling;
            if (next) {
                next.focus();
            } else {
                e.target.parentElement.firstElementChild.focus();
            }
            e.preventDefault();
            e.stopPropagation();
        }
    }
}